import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import {
  mockEntities,
  mockPeople,
  mockInterventions,
  mockOrgTeamMembers,
} from '../data/mockData';

export interface OrgDataResult<T> {
  data: T | null;
  isLoading: boolean;
  error: Error | null;
  isEmpty: boolean; // true when data is an empty array
}

export type OrgResource =
  | 'entities'
  | 'persons'
  | 'interventions'
  | 'events'
  | 'settings'
  | 'teamMembers';

const API_BASE_URL = process.env.REACT_APP_API_URL || '';

const RESOURCE_ENDPOINT_MAP: Record<OrgResource, string> = {
  entities: '/entities',
  persons: '/persons',
  interventions: '/interventions',
  events: '/events',
  settings: '/settings',
  teamMembers: '/org/members',
};

// List endpoints return a paginated envelope { items, nextCursor }. Settings (single object) and
// teamMembers ({ members, pendingInvitations }) are not paginated.
const PAGINATED_RESOURCES: ReadonlySet<OrgResource> = new Set([
  'entities',
  'persons',
  'interventions',
  'events',
]);

const PAGE_SIZE = 200;

/** Fetch a resource. For paginated resources, follow nextCursor and return the full array. */
async function fetchResource(endpoint: string, token: string | null, resource: OrgResource): Promise<unknown> {
  const headers = {
    Authorization: `Bearer ${token}`,
    'Content-Type': 'application/json',
  };

  if (!PAGINATED_RESOURCES.has(resource)) {
    const res = await fetch(`${API_BASE_URL}${endpoint}`, { headers });
    if (!res.ok) throw new Error(`API error: ${res.status} ${res.statusText}`);
    return res.json();
  }

  const items: unknown[] = [];
  let cursor: string | null = null;
  do {
    const query = `?limit=${PAGE_SIZE}${cursor ? `&cursor=${encodeURIComponent(cursor)}` : ''}`;
    const res = await fetch(`${API_BASE_URL}${endpoint}${query}`, { headers });
    if (!res.ok) throw new Error(`API error: ${res.status} ${res.statusText}`);
    const page = (await res.json()) as { items?: unknown[]; nextCursor?: string | null };
    if (Array.isArray(page.items)) items.push(...page.items);
    cursor = page.nextCursor ?? null;
  } while (cursor);
  return items;
}

function getMockData(resource: OrgResource): unknown {
  switch (resource) {
    case 'entities':
      return mockEntities;
    case 'persons':
      return mockPeople;
    case 'interventions':
      return mockInterventions;
    case 'events':
      return [];
    case 'settings':
      return null;
    case 'teamMembers':
      return mockOrgTeamMembers;
  }
}

export function useOrgData<T>(
  resource: OrgResource,
  options?: { mockFallback?: T }
): OrgDataResult<T> {
  const { isDemoUser, token, isAuthenticated } = useAuth();

  const [state, setState] = useState<OrgDataResult<T>>({
    data: null,
    isLoading: true,
    error: null,
    isEmpty: true,
  });

  useEffect(() => {
    // Demo user OR unauthenticated visitor: return mock data immediately, no API call
    if (isDemoUser || !isAuthenticated) {
      const mockData = getMockData(resource) as T;
      setState({
        data: mockData,
        isLoading: false,
        error: null,
        isEmpty: Array.isArray(mockData) && mockData.length === 0,
      });
      return;
    }

    // Authenticated real user: fetch from API
    let cancelled = false;

    setState(prev => ({ ...prev, isLoading: true, error: null }));

    const endpoint = RESOURCE_ENDPOINT_MAP[resource];
    fetchResource(endpoint, token, resource)
      .then((json: unknown) => {
        if (cancelled) return;
        setState({
          data: json as T,
          isLoading: false,
          error: null,
          isEmpty: Array.isArray(json) && json.length === 0,
        });
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        const error = err instanceof Error ? err : new Error(String(err));
        const fallback = options?.mockFallback ?? null;
        setState({
          data: fallback,
          isLoading: false,
          error,
          isEmpty: fallback === null || (Array.isArray(fallback) && fallback.length === 0),
        });
      });

    return () => {
      cancelled = true;
    };
  }, [isDemoUser, isAuthenticated, token, resource]); // eslint-disable-line react-hooks/exhaustive-deps

  return state;
}

const WRITE_ENDPOINT_MAP: Record<OrgResource, string> = {
  entities: '/entities',
  persons: '/persons',
  interventions: '/interventions',
  events: '/events',
  settings: '/settings',
  teamMembers: '/org/members',
};

export function useOrgWrite(): {
  post: (resource: OrgResource, body: object) => Promise<any>;
  patch: (resource: OrgResource, id: string, body: object) => Promise<any>;
  remove: (resource: OrgResource, id: string) => Promise<any>;
  isDemoBlocked: boolean;
} {
  const { isDemoUser, token } = useAuth();

  const demoError = new Error('Write operations are not available in demo mode');

  const post = async (resource: OrgResource, body: object): Promise<any> => {
    if (isDemoUser) throw demoError;
    const res = await fetch(`${API_BASE_URL}${WRITE_ENDPOINT_MAP[resource]}`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(body),
    });
    if (!res.ok) throw new Error(`API error: ${res.status} ${res.statusText}`);
    return res.json();
  };

  const patch = async (resource: OrgResource, id: string, body: object): Promise<any> => {
    if (isDemoUser) throw demoError;
    const res = await fetch(`${API_BASE_URL}${WRITE_ENDPOINT_MAP[resource]}/${id}`, {
      method: 'PATCH',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(body),
    });
    if (!res.ok) throw new Error(`API error: ${res.status} ${res.statusText}`);
    return res.json();
  };

  const remove = async (resource: OrgResource, id: string): Promise<any> => {
    if (isDemoUser) throw demoError;
    const res = await fetch(`${API_BASE_URL}${WRITE_ENDPOINT_MAP[resource]}/${id}`, {
      method: 'DELETE',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });
    if (!res.ok) throw new Error(`API error: ${res.status} ${res.statusText}`);
    // DELETE may return 204 No Content
    if (res.status === 204) return null;
    return res.json();
  };

  return { post, patch, remove, isDemoBlocked: isDemoUser };
}
